<template>
    <div class="top-bar" tabindex="-1">
        <div class="top-bar-leading">
            <div class="top-bar-sidebar-button">
                <button type="button" class="top-bar-btn" :title="$t('Main menu')" @click="clickMenu">
                    <img src="@/assets/svg/menu_open.svg" class="icon-svg-perso" />
                </button>
            </div>
            <div v-if="loggedIn && activeNavigationSection" class="top-bar-current-section" :title="activeNavigationBreadcrumbTitle">
                <router-link to="/" class="top-bar-current-section-icon" :title="$t('Go to home')" :aria-label="$t('Go to home')">
                    <i class="fas fa-home"></i>
                </router-link>
                <i class="fa-solid fa-chevron-right chevron-icon" />
                <NavLink
                    v-if="sectionCrumbTarget"
                    :to="sectionCrumbTarget"
                    :text="activeNavigationSection.title"
                    class="top-bar-current-section-text"
                />
                <span v-else class="top-bar-current-section-text">
                    <span>{{ activeNavigationSection.title }}</span>
                </span>
                <template v-if="activeNavigationItem">
                    <i class="fa-solid fa-chevron-right chevron-icon" />
                    <NavLink
                        v-if="itemCrumbTarget"
                        :to="itemCrumbTarget"
                        :text="activeNavigationItem.title"
                        class="top-bar-current-section-text"
                    />
                    <span v-else class="top-bar-current-section-text">
                        <span>{{ activeNavigationItem.title }}</span>
                    </span>
                </template>
            </div>
        </div>
        <div v-if="loggedIn" class="top-bar-buttons">
            <LanguageSelectMenu />
            <div class="top-bar-icons">
                <UseLLmButton v-if="canUseLLm" @openAssistant="showChatAssistant" />
                <NotificationsButton
                    :visible-notifications-count="visibleNotificationsCount"
                    :can-notifications="canNotifications"
                    @notifications-count-change="onNotificationsCountChange"
                />
                <UserSettingsButton
                    :profile-name="profileName"
                    :profile-image="profileImage"
                    :username="username"
                    :can-admin="canAdmin"
                    :can-config-data-space="canConfigDataSpace"
                    :render-language="renderLanguage"
                    :get-language="actualLanguage"
                    @select-language="selectLanguage"
                    @open-config-modal="openDataSpaceConfigModal"
                    @open-logout-modal="openLogOutModal"
                />
            </div>
        </div>
        <div class="top-bar-icons" v-if="!loggedIn">
            <LanguageSelectMenu />
            <button type="button" @click="login" class="btn btn-primary btn-top-bar-login">
                <i class="fas fa-sign-in"></i> {{ $t("Login") }}
            </button>
        </div>
    </div>
</template>

<script lang="ts">
import { AuthController } from "@/control/auth";
import { NotificationsController } from "@/control/notifications";
import { defineComponent, markRaw } from "vue";
import { AVAILABLE_LANGUAGES, getLanguage, setLanguage } from "@/i18n";
import { AppEvents } from "@/control/app-events";
import LanguageSelectMenu from "../dropdowns/language/LanguageSelectMenu.vue";
import LogoutModalContent from "../modals/auth/LogoutModalContent.vue";
import UserSettingsButton from "@/components/buttons/TopBar/UserSettingsButton.vue";
import NotificationsButton from "../buttons/TopBar/NotificationsButton.vue";
import UseLLmButton from "@/components/buttons/TopBar/UseLLmButton.vue";
import NavLink from "@/components/navigation/NavLink.vue";
import { buildSidebarNavigation, getActiveSidebarItem, getActiveSidebarSection } from "@/utils/sidebar-navigation";
import type { SidebarNavigationItem, SidebarNavigationSection } from "@/utils/sidebar-navigation";

const REQUIRED_ADMIN_PERMISSIONS = ["mod.users", "admin.roles"];

type NotificationCountChangeOptions = {
    optimistic?: boolean;
};

export default defineComponent({
    components: {
        LanguageSelectMenu,
        UserSettingsButton,
        NotificationsButton,
        UseLLmButton,
        NavLink,
    },
    name: "TopBar",
    emits: ["toggle-menu", "openModal"],
    data() {
        return {
            platformName: import.meta.env.VITE__PLATFORM_NAME || "Platform",
            loggedIn: AuthController.isAuthenticated(),
            profileImage: AuthController.ProfileImage,
            profileName: AuthController.ProfileName || AuthController.Username || "",
            username: AuthController.Username || "",
            navigationVersion: 0,

            canNotifications: AuthController.hasPermission("notification.read"),
            notificationsCount: NotificationsController.UnreadNotificationsCount,
            notificationsCountOverride: null as number | null,

            canUseLLm: AuthController.hasPermission("llm.use") && AuthController.SidecarLLM.assistant,
            canAdmin: REQUIRED_ADMIN_PERMISSIONS.some((p) => AuthController.hasPermission(p)),
            canConfigDataSpace: AuthController.hasPermission("admin.ds"),
        };
    },
    methods: {
        selectLanguage: function () {
            const currentLang = getLanguage();
            const currentIndex = AVAILABLE_LANGUAGES.findIndex((l) => l.id === currentLang);
            const nextIndex = (currentIndex + 1) % AVAILABLE_LANGUAGES.length;
            setLanguage(AVAILABLE_LANGUAGES[nextIndex].id);
        },

        showChatAssistant: function () {
            this.$emit("openModal", "chat-assistant");
        },

        login: function () {
            this.$requireLogin();
        },

        openDataSpaceConfigModal: function () {
            AppEvents.Emit("ds-config-modal-open");
        },

        clickMenu: function () {
            this.$emit("toggle-menu");
        },

        onAuthChanged: function () {
            this.loggedIn = AuthController.isAuthenticated();
            this.profileName = AuthController.ProfileName || AuthController.Username || "";
            this.username = AuthController.Username || "";
            this.profileImage = AuthController.ProfileImage;
            this.canUseLLm = AuthController.hasPermission("llm.use") && AuthController.SidecarLLM.assistant;
            this.canNotifications = AuthController.hasPermission("notification.read");
            this.canAdmin = REQUIRED_ADMIN_PERMISSIONS.some((p) => AuthController.hasPermission(p));
            this.canConfigDataSpace = AuthController.hasPermission("admin.ds");
            this.navigationVersion += 1;

            if (!this.canNotifications) {
                this.notificationsCount = 0;
                this.notificationsCountOverride = null;
            }
        },

        renderName: function (name: string): string {
            return ((name + "").split(" ")[0] + "").split(",")[0] || "???";
        },

        renderLanguage: function (lang: string): string {
            for (const language of AVAILABLE_LANGUAGES) {
                if (language.id === lang) {
                    return language.name;
                }
            }
            return "-";
        },

        onNotificationsCountChange: function (count: number, options?: NotificationCountChangeOptions) {
            this.notificationsCount = count;
            this.notificationsCountOverride = options?.optimistic && count === 0 ? 0 : null;
        },

        openLogOutModal: function () {
            this.$openModal({
                title: this.$t("Confirm logout"),
                message: this.$t("Do you want to log out of the application?"),
                customComponent: markRaw(LogoutModalContent),
                displayFooter: false,
                positionIconHeader: "none",
                modalType: "info",
            });
        },
    },
    mounted: function () {
        this.$listenOnAppEvent("auth-status-changed", this.onAuthChanged.bind(this));
        this.$listenOnAppEvent("notification-count-update", () => {
            this.notificationsCount = NotificationsController.UnreadNotificationsCount;
            this.notificationsCountOverride = null;
        });
    },
    beforeUnmount: function () {},
    computed: {
        navigationSections: function (): SidebarNavigationSection[] {
            void this.navigationVersion;

            return buildSidebarNavigation({
                $t: (key: string) => this.$t(key),
                isAuthenticated: this.loggedIn,
                hasPermission: (permission: string) => AuthController.hasPermission(permission),
                clearingHouseEnabled: AuthController.ClearingHouseEnabled,
            });
        },

        activeNavigationSection: function (): SidebarNavigationSection | null {
            const page = this.$route?.name ? String(this.$route.name) : "";

            return getActiveSidebarSection(page, this.navigationSections);
        },

        activeNavigationItem: function (): SidebarNavigationItem | null {
            const page = this.$route?.name ? String(this.$route.name) : "";

            return getActiveSidebarItem(page, this.navigationSections);
        },

        currentRouteName: function (): string {
            return this.$route?.name ? String(this.$route.name) : "";
        },

        sectionCrumbTarget: function (): string {
            const routeName = this.activeNavigationSection?.route;
            if (!routeName || routeName === this.currentRouteName) {
                return "";
            }
            return this.$router.resolve({ name: routeName }).path;
        },

        itemCrumbTarget: function (): string {
            const routeName = this.activeNavigationItem?.route;
            if (!routeName || routeName === this.currentRouteName) {
                return "";
            }
            return this.$router.resolve({ name: routeName }).path;
        },

        activeNavigationBreadcrumbTitle: function (): string {
            if (!this.activeNavigationSection) return "";
            if (!this.activeNavigationItem) return this.activeNavigationSection.title;

            return `${this.activeNavigationSection.title} > ${this.activeNavigationItem.title}`;
        },

        visibleNotificationsCount: function (): number {
            return this.notificationsCountOverride ?? this.notificationsCount;
        },

        platformNameParts: function () {
            if (this.platformName.includes("-")) {
                const [normalPlatformName, highlightedPlatformName] = this.platformName.split("-");
                return { normalPlatformName: normalPlatformName + "-", highlightedPlatformName };
            }
            return {
                normalPlatformName: this.platformName,
                highlightedPlatformName: "",
            };
        },
        actualLanguage: function () {
            return getLanguage();
        },
    },
});
</script>

<style>
.top-bar-user-menu .dropdown-menu-item.has-separator {
    border-top: 1px solid var(--color-border-default);
    border-bottom: none !important;
}

.top-bar-user-menu .dropdown-menu-item {
    padding: 16px;
}
</style>
